'use client'

import { IconToggleRadioDefault } from 'icons/components/IconToggleRadioDefault'
import { IconToggleRadioOn } from 'icons/components/IconToggleRadioOn'
import { type FormEvent, type ReactNode, useEffect, useState } from 'react'
import { cx } from 'utils/cx'

import { ErrorMessage } from './ErrorMessage'

import fukkuClasses from 'fukku/styles/classes.module.scss'
import styles from './Radio.module.scss'

export interface RadioOption {
	value: string | number
	content: ReactNode | string
	action?: ReactNode
	disabled?: boolean
}

export interface RadioProps {
	readonly id: string
	readonly className?: string
	readonly 'data-testid'?: string
	readonly disabled?: boolean
	readonly name: string
	readonly legend: string
	readonly onChange?: (value: string | number) => void
	readonly options: RadioOption[]
	readonly defaultSelected?: string | number
	readonly title?: string
	readonly errorMessage?: string
	readonly errorPlaceholders?: Record<string, string | number>
}

export const Radio = (props: RadioProps): ReactNode => {
	const {
		id,
		'data-testid': dataTestId,
		className = '',
		disabled = false,
		name,
		legend,
		onChange = () => {
			/* do nothing */
		},
		options,
		defaultSelected,
		title,
		errorMessage,
		errorPlaceholders,
	} = props

	const errorMessageId = `${id}-error`
	const defaultSelectedId = defaultSelected
		? `${name}-${defaultSelected}`
		: null

	const [selectedId, setSelectedId] = useState<string | null>(defaultSelectedId)

	useEffect(() => {
		setSelectedId(defaultSelectedId)
	}, [defaultSelectedId])

	const changeHandler = (e: FormEvent<HTMLFieldSetElement>) => {
		const target = e.target as HTMLInputElement

		// Needed to avoid errors when extended content has inputs with its own onChange events
		if (target.type === 'radio') {
			setSelectedId(target?.id)
			onChange(target?.value)
		}
	}

	return (
		<>
			<fieldset
				className={cx(styles.wrapper, className)}
				onChange={changeHandler}
				id={id}
				data-testid={dataTestId}
			>
				<legend className={fukkuClasses.srOnly}>{legend}</legend>
				{title && <p className={styles.title}>{title}</p>}
				{options.map((item) => {
					const optionId = `${name}-${item.value}`
					const selected = selectedId === optionId
					const updatedDataTestId = `${dataTestId}-${item.value}`
					const itemDisabled = disabled || item.disabled

					return (
						<div
							key={optionId}
							className={cx(styles.option, itemDisabled && styles.disabled)}
						>
							<label
								id={`${optionId}-label`}
								className={styles.radio}
								htmlFor={optionId}
							>
								<input
									className={fukkuClasses.srOnly}
									defaultValue={item.value}
									name={name}
									type='radio'
									id={optionId}
									disabled={itemDisabled}
									defaultChecked={selected}
									data-testid={updatedDataTestId}
								/>
								<div className={styles.icon}>
									{selected ? (
										<IconToggleRadioOn />
									) : (
										<IconToggleRadioDefault />
									)}
								</div>
								<span className={styles.center}>{item.content}</span>
							</label>
							{item.action}
						</div>
					)
				})}
			</fieldset>
			{errorMessage && (
				<ErrorMessage id={errorMessageId} errorPlaceholders={errorPlaceholders}>
					{errorMessage}
				</ErrorMessage>
			)}
		</>
	)
}
